import React from "react";
import {
  ArrowRightToLine,
  Bed,
  BookOpen,
  ConciergeBell,
  MessageCircleQuestion,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  GuestExperienceStepStatus,
  useGuestExperienceSidebarData,
} from "@/components/pages/dashboard/hooks/useGuestExperienceSidebarData";

const guestAppChildIcons: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  Services: ConciergeBell,
  Amenities: Bed,
  FAQ: MessageCircleQuestion,
  Pages: BookOpen,
};

const headingClass =
  "font-geist text-base font-medium leading-normal tracking-normal text-neutral-600";
const titleClass =
  "font-geist text-sm font-normal leading-none tracking-normal text-neutral-600";
const subtitleClass =
  "font-geist text-xs font-normal leading-none tracking-normal text-neutral-400";

const statusDotClassName: Record<GuestExperienceStepStatus, string> = {
  active: "border-secondary-badge bg-[#B8D4B8]",
  warning: "border-amber-200 bg-amber-300",
  error: "border-red-200 bg-red-400",
  loading: "border-zinc-200 bg-zinc-300",
  off: "border-zinc-200 bg-zinc-300",
};

export const GuestExperienceSidebar: React.FC<{
  className?: string;
  onClosePanel: () => void;
}> = ({ className, onClosePanel }) => {
  const { steps } = useGuestExperienceSidebarData();

  return (
    <div
      className={cn(
        "font-geist flex flex-col gap-2 px-3 pt-6 pb-6 text-neutral-600",
        className,
      )}
    >
      <div className="flex min-h-16 items-center gap-2">
        <Button
          type="button"
          variant="new_ghost"
          size="icon_sm"
          className="shrink-0 bg-transparent text-neutral-600"
          aria-label="Close guest experience panel"
          onClick={onClosePanel}
        >
          <ArrowRightToLine className="size-5" aria-hidden />
        </Button>
        <h2 className={headingClass}>Guest Experience</h2>
      </div>
      <ul className="space-y-5">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          return (
            <li key={step.title}>
              <div className="flex min-h-11 items-stretch gap-2">
                <div className="flex min-h-0 w-4 shrink-0 flex-col items-center self-stretch">
                  <span
                    className={cn(
                      "size-2.5 shrink-0 rounded-full border-2",
                      statusDotClassName[step.status],
                    )}
                    aria-hidden
                  />
                  {!isLast ? (
                    <div
                      className="mt-1.5 min-h-[2px] w-px flex-1 bg-zinc-200"
                      aria-hidden
                    />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-2">
                    <p className={titleClass}>{step.title}</p>
                    {step.subtitle !== "" ? (
                      <p className={subtitleClass}>{step.subtitle}</p>
                    ) : null}
                  </div>
                  {step.children ? (
                    <ul className="mt-4 space-y-4">
                      {step.children.map((child) => {
                        const Icon =
                          guestAppChildIcons[child.title] ?? ConciergeBell;
                        return (
                          <li key={child.title}>
                            <div className="flex items-start gap-2">
                              <Icon
                                className={cn(
                                  "mt-0.5 size-4 shrink-0",
                                  child.status === "active"
                                    ? "text-neutral-600"
                                    : "text-amber-500",
                                )}
                                aria-hidden
                              />
                              <div className="flex min-w-0 flex-col gap-2">
                                <p className={titleClass}>{child.title}</p>
                                <p className={subtitleClass}>
                                  {child.subtitle}
                                </p>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
